import { LinkTo } from '@ember/routing';
import Component from '@glimmer/component';
import { getRequestState } from '@warp-drive/core/reactive';
import LoanSummaryChart from 'tio-employee/components/charts/loan-summary-chart';
import { t } from 'ember-intl';
import Tile from '../tile';
import type { PersonRequest } from 'tio-employee/routes/authenticated/dashboard';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import safeFormatNumber from 'tio-common/helpers/safe-format-number';

export interface LoanSummarySignature {
  Args: {
    /**
     * The in-flight `/people/:id` request (accounts and loans) the dashboard route starts
     * without awaiting. The tile resolves it reactively: skeleton while pending, the
     * unavailable notice if it rejects, balance and chart once it resolves.
     */
    personRequest?: PersonRequest;
  };
}

export default class LoanSummary extends Component<LoanSummarySignature> {
  get requestState() {
    return this.args.personRequest ? getRequestState(this.args.personRequest) : undefined;
  }

  get isLoading() {
    return this.requestState?.isPending ?? false;
  }

  get isError() {
    return this.requestState?.isError ?? false;
  }

  get hasLoaded() {
    return !this.isLoading && !this.isError;
  }

  get person() {
    return this.requestState?.result?.data;
  }

  get loans() {
    return this.person?.activeAccounts.map((account) => account.activeLoans).flat() || [];
  }

  get lastUpdatedOn() {
    return this.loans[0]?.lastUpdatedOn;
  }

  get loansTotal() {
    return this.loans.reduce((sum, loan) => {
      return sum + loan.currentBalance;
    }, 0);
  }

  <template>
    <LinkTo @route="authenticated.account-activity.accounts">
      <Tile @headerText={{t "dashboard_tiles.loan_summary"}}>
        <:description>
          {{#if this.isLoading}}
            <div class="animate-pulse" aria-busy="true" data-test-loan-summary-loading>
              <div class="tio-bg-sunken rounded-lg h-4 w-24 mb-2"></div>
              <div class="tio-bg-sunken rounded-lg h-8 w-40"></div>
            </div>
          {{else if this.isError}}
            <p class="tio-text-sm-normal tio-fg-muted" data-test-loan-summary-unavailable>
              {{t "dashboard_tiles.loan_summary_unavailable"}}
            </p>
          {{else}}
            <p class="font-semibold">
              <span class="block uppercase text-sm">{{t "current_balance"}}</span>
              <span class="text-2xl">
                {{safeFormatNumber this.loansTotal style="currency" currency="USD"}}
              </span>
            </p>
            {{#if this.lastUpdatedOn}}
              <p class="text-xs tio-fg-muted">
                {{t "dashboard_tiles.last_updated"}}
                {{safeFormatDate this.lastUpdatedOn month="short" day="2-digit" year="numeric"}}
              </p>
            {{/if}}
          {{/if}}
        </:description>
        <:image>
          {{#if this.isLoading}}
            <div class="animate-pulse tio-bg-sunken rounded-full h-24 w-24"></div>
          {{else if this.hasLoaded}}
            <LoanSummaryChart @loans={{this.loans}} />
          {{/if}}
        </:image>
      </Tile>
    </LinkTo>
  </template>
}
